/**
 * Billing-related settings types/constants previously defined in
 * redo/model/src/team.ts. Lives in billing/common so the rest of
 * redo/billing/common/ can reference them without depending on redo/model.
 * redo/model/src/team-billing.ts re-exports for back-compat.
 */

import type { BaseTeam } from "@redotech/account-common/base-team";
import type { Stripe } from "stripe";
import { PaymentProviderType } from "./billing-engine-types";
import type { PaymentMethod } from "./payout";
import { BillingPeriod } from "./service-invoice";
import { OrderTrackingInvoiceBillingSettings } from "./tracking/tracking-billing-types";

/**
 * Shared base for product subscription-billing settings (IMS, AEO, exchange
 * agent, etc.).
 *
 * Keep in sync with the generated subscription-billing `*Raw` types in
 * `@redotech/backend-db-schema` (e.g. `ImsSubscriptionBillingRaw`,
 * `ImsForecastingBillingRaw`), which are produced from
 * `redo/backend/db/schema/src/team/SubscriptionBillingSchema.ts`.
 */
export interface BaseSubscriptionBillingSettings {
  enabled: boolean;
  startDate?: Date | null;
  period: BillingPeriod;
  periodFeeInCents: number;
  renewOnPeriodEnd: boolean;
  paymentProvider?: PaymentProviderType;
}

export const DEFAULT_EXCHANGE_AGENT_USAGE_FEE = 3.5;
export const DEFAULT_EXCHANGE_AGENT_USAGE_FEE_PERCENT = 0;

export const exchangeAgentBillingFeeType = ["flat", "percentage"] as const;
export type ExchangeAgentBillingFeeType =
  (typeof exchangeAgentBillingFeeType)[number];
export const ExchangeAgentBillingFeeType = exchangeAgentBillingFeeType;

export const exchangeAgentBillingType = ["shopify", "payout_order"] as const;
export type ExchangeAgentBillingType =
  (typeof exchangeAgentBillingType)[number];
export const ExchangeAgentBillingType = exchangeAgentBillingType;

export enum SupportBaseBillingVersion {
  FLAT_RATE = "flat_rate",
  BUCKETED_USAGE = "bucketed_usage",
  /** Simple per-ticket pricing for human-handled tickets */
  SIMPLE_HUMAN_USAGE = "simple_human_usage",
  /** Per-order billing - charges per order using the same logic as checkout optimization */
  PER_ORDER = "per_order",
}

export interface OrderTrackingBillingSettings {
  enabled: boolean;
  invoiceBilling?: OrderTrackingInvoiceBillingSettings | null | undefined;
  continueIfNotAccepted: boolean;
  rerouteBillingToMarketing?: boolean;
  pricePerOrder: number;
  freeOrdersPerMonth: number;
  freeUntilDateEnabled: boolean | null;
  freeUntilDate: string | null;
  texts: { pricePerSms: number; pricePerMms: number };
  unbilledUsage?: {
    orderCount?: number | null;
    smsCount?: number | null;
    mmsCount?: number | null;
  } | null;
}

/** How a merchant is billed. */
export type MerchantBillingMethod = "stripe" | "fake";

export interface StripeData {
  customer_id: string;
  card?: Stripe.Card | null;
  paymentMethod?: PaymentMethod;
  secondaryPaymentMethod?: PaymentMethod;
  primaryPendingVerificationClientSecret?: string | null;
  secondaryPendingVerificationClientSecret?: string | null;
}

export interface TeamBilling {
  stripe?: StripeData;
  billingMethod: MerchantBillingMethod;
  billingFailure: "allow" | "restrict";
  billingAutomation: "auto" | "manual";
  /**
   * Whether automated failed-billing (dunning) reminder emails are sent to
   * this merchant. Defaults to enabled when unset; set to "disabled" for
   * merchants whose collections are handled separately with finance.
   */
  failedBillingEmails?: "enabled" | "disabled";
  manualBillingDueInDays: number;
  merchantPricePerOrder?: string;
}
/** A `BaseTeam` projected with its top-level billing fields. */
export type BillingTeam = BaseTeam & TeamBilling;

/** A `BillingTeam` projected with a specific settings slice. */
export type BillingTeamWith<S> = BillingTeam & { settings: S };

/**
 * Team billing schedule configuration.
 *
 * Keep in sync with the generated `BillingSettings` subdocument in
 * `redo/backend/db/schema/src/Team.ts`.
 */
export interface BillingSettings {
  schedule: "bimonthly" | "weekly" | "monthly";
  dayOfWeek?: number;
  firstBillingDayOfMonth?: number;
  /** Day of month for monthly billing (1-28) */
  billingDayOfMonth?: number | null;
  chargeProcessingFee: boolean;
}

/**
 * AI Exchange (exchange agent) billing settings.
 *
 * Keep in sync with `ExchangeAgentBillingSettingsRaw`
 * (redo/backend/db/schema/src/types/team/ExchangeAgentSettings.schema.d.ts).
 */
export interface ExchangeAgentBillingSettings extends BaseSubscriptionBillingSettings {
  /** Is AI Exchange allowed at all? */
  enabled: boolean;
  /** Are we trying to bill for AI Exchange? */
  enableBilling: boolean;
  /** Flat fee per exchange (used when billingFeeType is "flat") */
  usageFee: number;
  /** Percentage of exchange value to bill (used when billingFeeType is "percentage") */
  usageFeePercent: number;
  /** Whether to bill flat fee per exchange or percentage of exchange value */
  billingFeeType: ExchangeAgentBillingFeeType;
  /** Billing method: "shopify" for Shopify App Usage billing, "payout_order" for per-exchange payout orders */
  billingType: ExchangeAgentBillingType;
}
